import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { useAuth } from "../../providers/AuthContext";
import { createChargingConnector, getConnectorStandards } from "../../api/ChargingConnector";
import { getAllChargingPoints, getChargingPoint } from "../../api/ChargingPoint";
import { getChargingSite } from "../../api/ChargingSites";
import { getSimpleTariffsByOperatorOrganizationPublicId } from "../../api/Tariffs";
import toast from "react-hot-toast";
import LoadingTablePage from "../LoadingTablePage";
import ConnectorForm from "../../components/connector/ConnectorForm";
import { useTranslation } from "react-i18next";

const CreateConnectorPage = () => {
    const { t } = useTranslation();
    const { getAccessToken } = useAuth();
    const location = useLocation();
    const { chargingPointPublicId, chargingSitePublicId, operatorOrganizationPublicId } =
        location.state || {};
    const navigate = useNavigate();

    const [chargingPoints, setChargingPoints] = useState([]);
    const [tariffs, setTariffs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [invalidFields, setInvalidFields] = useState([]);

    const [chargingConnector, setChargingConnector] = useState({
        chargingPointPublicId: chargingPointPublicId || "",
        chargingConnectorNumber: null,
        chargingConnectorName: "",
        maxElectricPower: null,
        maxAmperage: null,
        maxVoltage: null,
        format: "",
        powerType: "",
        standard: "",
        currentType: "",
        termsAndConditions: "",
        version: 0,
        images: [],
        parkingRestrictions: [],
        directions: [],
        statusSchedules: [],
        capabilities: [],
        evseCapabilities: [],
        tariffIds: [],
    });
    const [connectorStandards, setConnectorStandards] = useState([]);
    const selectedPointIdRef = useRef(chargingConnector.chargingPointPublicId);
    selectedPointIdRef.current = chargingConnector.chargingPointPublicId;

    useEffect(() => {
        const fetchChargingPoints = async () => {
            setLoading(true);
            try {
                const token = await getAccessToken();
                if (chargingPointPublicId) {
                    const point = await getChargingPoint(chargingPointPublicId, token);
                    setChargingPoints(point ? [point] : []);
                    return;
                }
                const points = await getAllChargingPoints(token);
                setChargingPoints(points);
            } catch {
                toast.error(t("toast.failedFetch", { entity: t("entity.points") }));
            } finally {
                setLoading(false);
            }
        };
        fetchChargingPoints();
    }, [getAccessToken, chargingPointPublicId]);

    // Prefill chargingPointPublicId
    useEffect(() => {
        if (chargingPointPublicId) {
            setChargingConnector(prev => ({ ...prev, chargingPointPublicId }));
        }
    }, [chargingPointPublicId]);

    const handleInputChange = (key, value) => {
        setHasChanges(true);
        setChargingConnector((prev) => {
            if (key === "chargingPointPublicId" && value !== prev.chargingPointPublicId) {
                return { ...prev, chargingPointPublicId: value, tariffIds: [] };
            }
            return { ...prev, [key]: value };
        });
    };

    useEffect(() => {
        const fetchConnectorStandards = async () => {
            try {
                const token = await getAccessToken();
                const standards = await getConnectorStandards(token);
                setConnectorStandards(standards);
            } catch {
                toast.error(t("toast.failedFetch", { entity: t("form.standard") }));
            }
        };

        fetchConnectorStandards();
    }, [getAccessToken]);

    useEffect(() => {
        let cancelled = false;
        const selectedPointId = chargingConnector.chargingPointPublicId;
        setTariffs([]);

        if (!selectedPointId) {
            return () => {
                cancelled = true;
            };
        }

        const isStale = () =>
            cancelled || selectedPointIdRef.current !== selectedPointId;

        const loadTariffs = async () => {
            try {
                const token = await getAccessToken();
                if (isStale()) {
                    return;
                }

                let operatorOrgPublicId = operatorOrganizationPublicId ?? null;

                const parentPoint = chargingPoints.find(
                    (point) => point.chargingPointPublicId === selectedPointId
                );
                const sitePublicId =
                    parentPoint?.chargingSitePublicId || chargingSitePublicId || null;

                if (sitePublicId) {
                    const site = await getChargingSite(sitePublicId, token);
                    if (isStale()) {
                        return;
                    }
                    operatorOrgPublicId =
                        site?.operator?.organizationPublicId ?? operatorOrgPublicId;
                }

                if (!operatorOrgPublicId) {
                    return;
                }

                const tariffsData = await getSimpleTariffsByOperatorOrganizationPublicId(
                    operatorOrgPublicId,
                    token
                );
                if (isStale()) {
                    return;
                }
                setTariffs(tariffsData || []);
            } catch {
                if (isStale()) {
                    return;
                }
                toast.error(t("toast.failedFetch", { entity: t("entity.tariffs") }));
            }
        };

        loadTariffs();

        return () => {
            cancelled = true;
        };
    }, [
        chargingConnector.chargingPointPublicId,
        chargingPoints,
        chargingSitePublicId,
        operatorOrganizationPublicId,
        getAccessToken,
    ]);

    const validateConnectorBeforeSave = (connector) => {
        const missing = [];
        if (!connector.chargingPointPublicId) missing.push("chargingPointPublicId");
        if (!connector.chargingConnectorName?.trim()) missing.push("chargingConnectorName");
        if (!connector.standard?.trim()) missing.push("standard");
        if (!connector.format?.trim()) missing.push("format");
        if (!connector.powerType?.trim()) missing.push("powerType");
        if (!connector.chargingConnectorNumber || connector.chargingConnectorNumber <= 0)
            missing.push("chargingConnectorNumber");
        if (!connector.maxVoltage || connector.maxVoltage < 1) missing.push("maxVoltage");
        if (!connector.maxAmperage || connector.maxAmperage < 1) missing.push("maxAmperage");
        if (!connector.maxElectricPower || connector.maxElectricPower < 1) missing.push("maxElectricPower");
        if (!connector.tariffIds?.length) missing.push("tariffIds");
        return missing;
    };

    const handleCreateChargingConnector = async () => {
        const missing = validateConnectorBeforeSave(chargingConnector);
        if (missing.length > 0) {
            setInvalidFields(missing);
            toast.error(t("common.pleaseFillRequiredFields", { fields: missing.join(", ") }));
            return;
        }
        setInvalidFields([]);
        setLoading(true);
        const toastId = toast.loading(t("toast.creating", { entity: t("entity.connector") }));

        try {
            const token = await getAccessToken();
            const payload = {
                ...chargingConnector,
                maxElectricPower: Number(chargingConnector.maxElectricPower),
                maxAmperage: Number(chargingConnector.maxAmperage),
                maxVoltage: Number(chargingConnector.maxVoltage),
                chargingConnectorNumber: Number(chargingConnector.chargingConnectorNumber),
                tariffIds: chargingConnector.tariffIds ?? [],
            };

            const newConnector = await createChargingConnector(payload, token);

            toast.success(t("toast.created", { entity: t("entity.connector") }), { id: toastId });
            setHasChanges(false);

            // Navigate to newly created connector
            navigate(`/connector/${newConnector.chargingConnectorPublicId}?tab=details`, {
                state: { chargingPointPublicId, chargingSitePublicId, operatorOrganizationPublicId },
            });
        } catch (err) {
            console.error("Failed to create charging connector", err);
            toast.error(err?.message, { id: toastId });
        } finally {
            setLoading(false);
        }
    };

    const navigateBack = () => {
        if (chargingSitePublicId) navigate(`/site/${chargingSitePublicId}`);
        else if (chargingPointPublicId) navigate(`/point/${chargingPointPublicId}?tab=connectors`);
        else navigate("/connectors");
    };

    return (
        <>
            <HeaderBar />
            <div className="flex flex-row w-full">
                <Sidebar />
                <main className="page relative">
                    <div className="absolute inset-0 pointer-events-none">
                        <LoadingTablePage title={t("pages.chargingConnectors")} loading={loading} />
                    </div>

                    <div className={`${loading ? "loading" : ""} page-content`}>
                        <div className="flex flex-row items-center pb-4">
                            <button onClick={navigateBack} className="text-black flex">
                                <FontAwesomeIcon icon={faArrowLeft} className="w-4 pr-4" />
                            </button>
                            <h2 className="border-l border-mute-200 pl-4">{t("pages.createConnector")}</h2>
                            <div className="flex gap-4 ml-auto">
                                <button className="btn btn-outline-primary" onClick={navigateBack}>
                                    {t("common.cancel")}
                                </button>
                                <button
                                    className="btn btn-primary"
                                    disabled={!hasChanges}
                                    onClick={handleCreateChargingConnector}
                                >
                                    {t("common.save")}
                                </button>
                            </div>
                        </div>

                        <div className="px-4 py-8 shadow-lg rounded-md w-full">
                            <ConnectorForm
                                chargingConnector={chargingConnector}
                                handleInputChange={handleInputChange}
                                chargingPoints={chargingPoints}
                                invalidFields={invalidFields}
                                standards={connectorStandards}
                                tariffs={tariffs}
                            />
                        </div>
                    </div>
                </main>
            </div>
        </>
    );
};

export default CreateConnectorPage;
